import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/providers/AuthProvider';
import { roleSatisfies } from '@/lib/roleRank';
import { consoleApi, type ModerationQueueItem } from '@/lib/consoleApi';
import { describeAutoReview, describeAutoReviewError } from '@/lib/autoReviewSummary';
import { AdminPage, RegistryTable } from '@/admin';

export function ModerationQueuePage() {
  const qc = useQueryClient();
  const { role } = useAuth();
  const isAdmin = roleSatisfies(role ?? '', 'admin');
  const q = useQuery({ queryKey: ['moderation-queue'], queryFn: () => consoleApi.moderation.queue() });
  const budget = useQuery({ queryKey: ['moderation-budget'], queryFn: () => consoleApi.moderation.budgetStatus(), enabled: isAdmin });

  const refresh = () => void qc.invalidateQueries({ queryKey: ['moderation-queue'] });
  const batch = useMutation({
    mutationFn: () => consoleApi.moderation.autoReviewBatch(),
    onSuccess: (r) => { refresh(); alert(describeAutoReview(r)); },
    onError: (e: unknown) => { refresh(); alert(describeAutoReviewError(e)); },
  });
  const requeue = useMutation({
    mutationFn: () => consoleApi.moderation.requeueRejects(),
    onSuccess: (r) => { refresh(); alert(`Requeued:\n${JSON.stringify(r, null, 2).slice(0, 400)}`); },
    onError: (e: unknown) => alert(e instanceof Error ? e.message : 'Requeue failed'),
  });

  const b = budget.data;

  return (
    <AdminPage
      title="Moderation queue"
      description="Research documents awaiting review before they enter the Research Database."
      actions={isAdmin ? (
        <>
          <Button variant="outline" size="sm" data-testid="moderation-run-auto-review" disabled={batch.isPending} onClick={() => { if (confirm('Run auto-review over the pending queue? This calls the model on each document.')) batch.mutate(); }}>{batch.isPending ? 'Reviewing…' : 'Run auto-review'}</Button>
          <Button variant="outline" size="sm" disabled={requeue.isPending} onClick={() => { if (confirm('Requeue previously auto-rejected documents?')) requeue.mutate(); }}>{requeue.isPending ? 'Requeuing…' : 'Requeue rejects'}</Button>
        </>
      ) : undefined}
    >
      {isAdmin && b ? (
        <div className="mb-13 text-body-sm text-text-secondary">
          {b.status === 'no_data'
            ? <span>Budget: no cost log yet.</span>
            : <>Spend to date <strong className="text-text-primary">${typeof b.total_spend_usd === 'number' ? b.total_spend_usd.toFixed(2) : '—'}</strong>{b.run_halted ? <span className="text-flagged"> · run halted (budget)</span> : ''}{b.pilot_complete ? <span className="text-success"> · pilot complete</span> : ''}</>}
        </div>
      ) : null}

      <RegistryTable<ModerationQueueItem>
        columns={[
          { key: 'doc', header: 'Document', render: (d) => (
            <Link to={`/moderation/${encodeURIComponent(d.document_id)}`} className="font-mono text-[12px] text-accent hover:underline">{d.document_id}</Link>
          ) },
          { key: 'size', header: 'Size', className: 'text-[12px] text-text-secondary', render: (d) => d.content_length ? `${d.content_length} bytes` : '—' },
        ]}
        rows={q.data ?? []}
        getRowKey={(d) => d.document_id}
        count={q.data?.length}
        state={{ loading: q.isLoading, error: q.isError }}
        emptyText="Queue is empty."
      />
    </AdminPage>
  );
}
